/**
 * Shared building blocks for the campaign editor experience — extracted
 * from CampaignEditor.tsx so both the create-mode page and the tabbed
 * CampaignDetail page render assets/preview/playing-hours identically
 * instead of maintaining two copies.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import { CampaignAsset, LayoutZone } from '../../types';

export const FULLSCREEN_FALLBACK: LayoutZone = {
  slug: 'main',
  x_pct: 0,
  y_pct: 0,
  w_pct: 100,
  h_pct: 100,
  accepts: ['video', 'image'],
};

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#64748B',
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

// Deterministic colour per zone slug — keeps preview rectangles
// readable when multiple zones share the stage.
export const COLOR_FOR_ZONE = (slug: string, alpha = 1): string => {
  const palette = [
    `rgba(11,166,223,${alpha})`, // sky
    `rgba(249,115,22,${alpha})`, // accent
    `rgba(22,163,74,${alpha})`, // green
    `rgba(168,85,247,${alpha})`, // purple
    `rgba(234,179,8,${alpha})`, // amber
  ];
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

interface LayoutOptionProps {
  label: string;
  slug: string;
  zones: LayoutZone[];
  selected: boolean;
  onSelect: () => void;
}

export const LayoutOption: React.FC<LayoutOptionProps> = ({
  label,
  slug,
  zones,
  selected,
  onSelect,
}) => (
  <Box
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onSelect();
      }
    }}
    sx={{
      cursor: 'pointer',
      borderRadius: 2,
      border: selected ? '2px solid #2563EB' : '1px solid #E2E8F0',
      p: 1.25,
      background: selected ? '#E0F4FF' : 'white',
      transition: 'all 120ms',
      '&:hover': { borderColor: '#2563EB' },
    }}
  >
    <Box
      sx={{
        aspectRatio: '16 / 9',
        position: 'relative',
        mb: 1,
        background: '#F8FAFC',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {zones.map((z, i) => (
        <Box
          key={`${z.slug}-${i}`}
          sx={{
            position: 'absolute',
            left: `${z.x_pct}%`,
            top: `${z.y_pct}%`,
            width: `${z.w_pct}%`,
            height: `${z.h_pct}%`,
            background: COLOR_FOR_ZONE(z.slug, 0.25),
            border: '1px solid rgba(11,166,223,0.5)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: '#1D4ED8',
            fontFamily: 'monospace',
          }}
        >
          {z.slug}
        </Box>
      ))}
    </Box>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 11, color: '#94A3B8', fontFamily: 'monospace' }}>
      {slug}
    </Typography>
  </Box>
);

/** Tiny editable seconds field. Local-state edit, commit-on-blur so the
 *  publisher can type "12" without the editor PATCHing on every digit.
 *  Guards against 0 / negative / NaN — those would either lock the
 *  player on a frame or trigger the safety watchdog. */
export const DurationField: React.FC<{
  asset: CampaignAsset;
  onChange: (seconds: number) => void | Promise<void>;
}> = ({ asset, onChange }) => {
  const [draft, setDraft] = useState(String(asset.duration_seconds || ''));
  useEffect(() => {
    setDraft(String(asset.duration_seconds || ''));
  }, [asset.duration_seconds]);
  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDraft(String(asset.duration_seconds || ''));
      return;
    }
    if (parsed === asset.duration_seconds) return;
    void onChange(parsed);
  };
  return (
    <TextField
      size="small"
      type="number"
      label="sec"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      inputProps={{ min: 1, max: 600, step: 1, style: { textAlign: 'right' } }}
      sx={{ width: 86 }}
    />
  );
};

interface AssetGridProps {
  zones: LayoutZone[];
  assets: CampaignAsset[];
  uploadingZone: string | null;
  onUpload: (file: File, zoneSlug: string) => Promise<void>;
  onAddByUrl: (url: string, zoneSlug: string) => Promise<void>;
  onRemove: (asset: CampaignAsset) => Promise<void>;
  // Persist a new duration_seconds for an existing asset. Editor wires
  // this to apiService.updateCampaignAsset; staged (pre-save) assets
  // get patched in-place on the page-level state. Lets publishers
  // override the auto-probed video runtime or set a non-default image
  // dwell without re-uploading.
  onDurationChange: (asset: CampaignAsset, seconds: number) => Promise<void>;
}

export const AssetGrid: React.FC<AssetGridProps> = ({
  zones,
  assets,
  uploadingZone,
  onUpload,
  onAddByUrl,
  onRemove,
  onDurationChange,
}) => {
  // Inline URL-entry dialog state — `zoneSlug` doubles as the open
  // flag (null = closed). We don't bother routing this through the
  // parent because the URL entry is a per-zone, transient interaction
  // that doesn't need to survive a parent re-render.
  const [urlDialogZone, setUrlDialogZone] = useState<string | null>(null);
  const [urlDraft, setUrlDraft] = useState('');
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const submitUrlDialog = async () => {
    if (!urlDialogZone) return;
    setUrlSubmitting(true);
    try {
      await onAddByUrl(urlDraft, urlDialogZone);
      setUrlDialogZone(null);
      setUrlDraft('');
    } finally {
      setUrlSubmitting(false);
    }
  };
  const grouped = useMemo(() => {
    const m: Record<string, CampaignAsset[]> = {};
    assets.forEach((a) => {
      (m[a.zone_slug] = m[a.zone_slug] || []).push(a);
    });
    return m;
  }, [assets]);

  return (
    <Stack spacing={1.5}>
      {zones.map((z) => {
        const items = grouped[z.slug] || [];
        return (
          <Paper
            key={z.slug}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: items.length ? 'background.paper' : '#F8FAFC',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {z.slug}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {z.w_pct.toFixed(0)}% × {z.h_pct.toFixed(0)}% of viewport
                  {z.accepts?.length ? ` · accepts ${z.accepts.join(' / ')}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  onClick={() => {
                    setUrlDraft('');
                    setUrlDialogZone(z.slug);
                  }}
                  disabled={uploadingZone !== null}
                  sx={{ textTransform: 'none' }}
                >
                  From URL
                </Button>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={
                    uploadingZone === z.slug ? (
                      <CircularProgress size={14} />
                    ) : (
                      <AddIcon />
                    )
                  }
                  disabled={uploadingZone !== null}
                >
                  {uploadingZone === z.slug ? 'Uploading…' : 'Add asset'}
                  <input
                    type="file"
                    accept="image/*,video/*"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUpload(f, z.slug);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Stack>
            </Stack>
            {items.map((a) => (
              <Stack
                key={a.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid #E2E8F0',
                  bgcolor: 'background.paper',
                  mt: 1,
                }}
              >
                {a.content_type === 'VIDEO' ? (
                  <VideocamIcon color="primary" />
                ) : (
                  <ImageIcon color="secondary" />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                    noWrap
                  >
                    {a.content_url}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.content_type} · v{a.version || 1}
                  </Typography>
                </Box>
                <DurationField
                  asset={a}
                  onChange={(sec) => onDurationChange(a, sec)}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => onRemove(a)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Paper>
        );
      })}

      <Dialog
        open={urlDialogZone !== null}
        onClose={() => !urlSubmitting && setUrlDialogZone(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add asset by URL
          {urlDialogZone && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontFamily: 'monospace' }}
            >
              · {urlDialogZone}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste the public URL of a video (.mp4 / .mov / .webm) or image
            (.jpg / .png / .webp). The file must already be reachable from
            the gb-media device — we do not download or re-upload it.
          </Typography>
          <TextField
            label="Content URL"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://cdn.example.com/ads/spring-promo.mp4"
            autoFocus
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !urlSubmitting) {
                e.preventDefault();
                void submitUrlDialog();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUrlDialogZone(null)}
            disabled={urlSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitUrlDialog()}
            disabled={urlSubmitting || urlDraft.trim().length === 0}
            startIcon={urlSubmitting ? <CircularProgress size={16} /> : null}
          >
            {urlSubmitting ? 'Adding…' : 'Add asset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

interface PreviewStageProps {
  zones: LayoutZone[];
  assets: CampaignAsset[];
  /** When true, each zone rotates through its assets per duration_seconds
   *  and videos auto-play. When false, the first asset of each zone is
   *  shown statically. */
  playing: boolean;
  /** Bumped to reset every zone's rotation back to index 0. */
  resetTick: number;
}

export const PreviewStage: React.FC<PreviewStageProps> = ({
  zones,
  assets,
  playing,
  resetTick,
}) => {
  const byZone = useMemo(() => {
    const m: Record<string, CampaignAsset[]> = {};
    assets.forEach((a) => {
      (m[a.zone_slug] = m[a.zone_slug] || []).push(a);
    });
    return m;
  }, [assets]);

  return (
    <Box
      sx={{
        aspectRatio: '16 / 9',
        background: '#0F172A',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.05)',
      }}
    >
      {zones.map((z, i) => {
        const items = byZone[z.slug] || [];
        const filled = items.length > 0;
        return (
          <Box
            key={`${z.slug}-${i}`}
            sx={{
              position: 'absolute',
              left: `${z.x_pct}%`,
              top: `${z.y_pct}%`,
              width: `${z.w_pct}%`,
              height: `${z.h_pct}%`,
              // Hard outline on every zone so the layout's geometry is
              // legible even when zones are empty (previously they
              // looked like content bleed).
              outline: '1px solid rgba(255,255,255,0.15)',
              outlineOffset: '-1px',
              // Use the deterministic per-slug colour as the empty-zone
              // background so an unfilled zone reads as "intentionally
              // pending" instead of "broken".
              background: filled ? '#000' : COLOR_FOR_ZONE(z.slug, 0.55),
              overflow: 'hidden',
            }}
          >
            {filled ? (
              <ZonePlayer
                zoneSlug={z.slug}
                items={items}
                playing={playing}
                resetTick={resetTick}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                  color: 'white',
                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 13,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                    }}
                  >
                    {z.slug}
                  </Typography>
                  <Typography sx={{ fontSize: 10, opacity: 0.85, mt: 0.5 }}>
                    no asset yet
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Slug badge — always visible so the user can tell which
                zone is which while video is playing, and it survives
                the cover-cropped image without obscuring much. */}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: 'white',
                fontSize: 9,
                fontWeight: 700,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                letterSpacing: '0.06em',
                pointerEvents: 'none',
              }}
            >
              {z.slug}
            </Box>

            {items.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'white',
                  fontSize: 9,
                  fontWeight: 700,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  pointerEvents: 'none',
                }}
              >
                +{items.length - 1} more
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

interface ZonePlayerProps {
  zoneSlug: string;
  items: CampaignAsset[];
  playing: boolean;
  resetTick: number;
}

/**
 * Per-zone rotation in the preview stage. Mirrors the gb-media
 * ZoneRenderer's responsibilities at a much smaller scale:
 *   - Cycle through `items` in order using each asset's
 *     `duration_seconds` for images and the `onEnded` event for videos.
 *   - When `playing` is false, freeze on the current index but keep
 *     the rendered content (so toggling to play doesn't blink).
 *   - A bump on `resetTick` returns every zone to index 0 — the
 *     restart-from-beginning button on the preview controls.
 */
const ZonePlayer: React.FC<ZonePlayerProps> = ({
  zoneSlug,
  items,
  playing,
  resetTick,
}) => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // External reset.
  useEffect(() => {
    setIndex(0);
  }, [resetTick]);

  // Restart from 0 whenever the items list changes (zone slug edits,
  // delete, new upload).
  useEffect(() => {
    setIndex(0);
  }, [items.length]);

  const advance = useCallback(() => {
    setIndex((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
  }, [items.length]);

  // Image timer. Videos advance on their own `onEnded` event so we
  // skip the timer for them; videos keep a safety fallback the same
  // way ZoneRenderer does on the player.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!playing || items.length === 0) return;
    const current = items[index % items.length];
    if (!current) return;
    const seconds =
      current.duration_seconds > 0
        ? current.duration_seconds
        : current.content_type === 'IMAGE'
        ? 8
        : 30;
    const safetyExtra = current.content_type === 'VIDEO' ? 5 : 0;
    timerRef.current = setTimeout(advance, (seconds + safetyExtra) * 1000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [playing, items, index, advance]);

  if (items.length === 0) return null;
  const current = items[index % items.length];

  if (current.content_type === 'IMAGE') {
    return (
      <Box
        component="img"
        src={current.content_url}
        alt={zoneSlug}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  }

  return (
    <Box
      component="video"
      key={current.id /* force reload when index changes */}
      src={current.content_url}
      autoPlay={playing}
      muted
      playsInline
      onEnded={advance}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        bgcolor: 'black',
      }}
    />
  );
};

// ---- playing-hours editor ----

const DAYS: { key: string; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

interface DayWindow {
  on: boolean;
  start: string;
  end: string;
}

const parsePlayingHours = (csv: string): Record<string, DayWindow> => {
  const out: Record<string, DayWindow> = {};
  DAYS.forEach((d) => (out[d.key] = { on: false, start: '08:00', end: '22:00' }));
  if (!csv) return out;
  csv.split(',').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq <= 0) return;
    const day = part.slice(0, eq).trim().toLowerCase();
    const window = part.slice(eq + 1).trim();
    const dash = window.indexOf('-');
    if (dash <= 0) return;
    const start = window.slice(0, dash).trim();
    const end = window.slice(dash + 1).trim();
    if (out[day]) {
      out[day] = { on: true, start, end };
    }
  });
  return out;
};

const serialisePlayingHours = (perDay: Record<string, DayWindow>): string =>
  DAYS.filter((d) => perDay[d.key]?.on)
    .map((d) => `${d.key}=${perDay[d.key].start}-${perDay[d.key].end}`)
    .join(',');

interface PlayingHoursEditorProps {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Playing-hours editor.
 *
 * Two states:
 *   - "All day, every day" (empty CSV — default for new campaigns).
 *     One switch enables the per-day grid; flipping it back wipes the
 *     CSV.
 *   - Per-day grid with on/off switch + start/end time pickers. When
 *     a day is off, the row is greyed and that day is dark.
 *
 * Persisted CSV format matches the backend exactly so no
 * client-side translation logic drifts.
 */
export const PlayingHoursEditor: React.FC<PlayingHoursEditorProps> = ({
  value,
  onChange,
}) => {
  const allDay = !value;
  const perDay = useMemo(() => parsePlayingHours(value), [value]);

  const setAllDay = (on: boolean) => {
    if (on) {
      onChange('');
    } else {
      // Bootstrap a sensible default — every day 08:00-22:00.
      const seed: Record<string, DayWindow> = {};
      DAYS.forEach((d) => (seed[d.key] = { on: true, start: '08:00', end: '22:00' }));
      onChange(serialisePlayingHours(seed));
    }
  };

  const updateDay = (day: string, patch: Partial<DayWindow>) => {
    const next = { ...perDay, [day]: { ...perDay[day], ...patch } };
    onChange(serialisePlayingHours(next));
  };

  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={allDay} onChange={(_, on) => setAllDay(on)} />}
        label={
          <Typography sx={{ fontWeight: 600 }}>
            All day, every day (24/7)
          </Typography>
        }
        sx={{ mb: 1 }}
      />
      {!allDay && (
        <Stack
          spacing={0.75}
          sx={{
            p: 1.5,
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            bgcolor: '#F8FAFC',
          }}
        >
          {DAYS.map((d) => {
            const w = perDay[d.key];
            return (
              <Stack
                key={d.key}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: w.on ? 'background.paper' : 'transparent',
                  opacity: w.on ? 1 : 0.55,
                }}
              >
                <Switch
                  size="small"
                  checked={w.on}
                  onChange={(_, on) => updateDay(d.key, { on })}
                />
                <Typography sx={{ width: 96, fontWeight: 600, fontSize: 13 }}>
                  {d.label}
                </Typography>
                <TextField
                  type="time"
                  size="small"
                  value={w.start}
                  disabled={!w.on}
                  onChange={(e) => updateDay(d.key, { start: e.target.value })}
                  inputProps={{ step: 300 }}
                  sx={{ width: 120 }}
                />
                <Typography sx={{ color: '#94A3B8', fontFamily: 'monospace' }}>
                  →
                </Typography>
                <TextField
                  type="time"
                  size="small"
                  value={w.end}
                  disabled={!w.on}
                  onChange={(e) => updateDay(d.key, { end: e.target.value })}
                  inputProps={{ step: 300 }}
                  sx={{ width: 120 }}
                />
              </Stack>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
            Single window per day. For split slots (e.g. lunch + dinner),
            run two campaigns with the same creative.
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
